import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _dec, _class, _descriptor, _ForwardFocusRef;
/**
 * Created by jhoel on 6/8/2018.
 */

import { NullUtils } from "abweb/utils/NullUtils";
import { DeferredValuePromise, PromiseUtils } from "abweb/utils/PromiseUtils";
import { action, makeObservable, observable } from "mobx";

/** Utility methods relating to focus.
 * By having a layer of indirection that claims focus on a control, it's easier
 * to debug situations where components are stealing focus from each other.
 */
export class FocusUtils {
  /**
   * This is a helper function that can safely be called to set focus from
   * inside React lifecycle methods. The regular focus methods do not always
   * work in Chrome (as of March 2018).
   * @author aarmstrong? (Pulled from AbPopup)
   */
  static setFocus(elementOrRef, logDescription = "NoDescriptionGiven", disableBlurBeforeFocus = false) {
    const element = elementOrRef && "current" in elementOrRef ? elementOrRef.current : elementOrRef;
    if (!element) {
      return;
    }

    // console.log("FocusUtils setFocus: starting with focus on", document.activeElement);

    // ABSDK-1598 and EI-15109
    // Can fix the issue with the race condition on blur and focus
    // by moving element.blur() inside the requestAnimationFrame
    // No one's sure why this isn't in an animation frame. Perhaps to fix a chrome bug from 2018?
    // It should be fine to move this into the animation frame, it'll just blur very slightly later
    // This utility gets used in lots of places though, it may produce side effects.
    if (!disableBlurBeforeFocus) {
      element.blur();
    }
    window.requestAnimationFrame(() => {
      //console.log(`invoking focus on `, element); //`
      element.focus();
      //console.log("focus is now ", document.activeElement);
    });

    // const strategy: number = 1
    // console.log(`FocusUtils setFocus: strategy ${strategy}`); //`

    // if (strategy === 1)
    // {
    //   // console.log(`FocusUtils setFocus: first, invoking blur on ${logDescription}`, element); //`
    //   element.blur();
    //   window.requestAnimationFrame(() => {
    //       //console.log(`FocusUtils setFocus: second, invoking focus on `, element); //`
    //       element.focus();
    //       //console.log("FocusUtils setFocus: last, focus is now ", document.activeElement);
    //   });
    // }
    // if (strategy === 2)
    // {
    //   console.log(`FocusUtils setFocus: first, invoking blur on ${logDescription}`, element); //`
    //   element.blur();
    //   console.log(`FocusUtils setFocus: second, invoking focus on `, element); //`
    //   element.focus();
    //   console.log("FocusUtils setFocus: last, focus is now ", document.activeElement);
    // }
    // if (strategy === 3) // bug occurs
    // {
    //   console.log(`FocusUtils setFocus: first, invoking blur on ${logDescription}`, element); //`
    //   element.blur();
    //   (async () => { // IIFE
    //     await PromiseUtils.sleepZero()
    //     console.log(`FocusUtils setFocus: second invoking focus on `, element); //`
    //     element.focus();
    //     console.log("FocusUtils setFocus: last, focus is now ", document.activeElement);
    //   })()
    // }
    // if (strategy === 4)
    // {
    //   // element.blur();  // skip the blur, just set focus immediately
    //   console.log(`FocusUtils setFocus: invoking focus on `, element); //`
    //   element.focus();
    //   console.log("FocusUtils setFocus: last, focus is now ", document.activeElement);
    // }
  }
  /**
   * Debugging function to help watch when something new takes focus.
   * It doesn't locate the root cause, but helps to point you in the right direction.
   * Debugging aid only - not meant for application code!
   */
  static logFocusOnInterval_DEBUG(frequencyMsecs) {
    setInterval(function () {
      //console.log("interval focus 2", document.activeElement);
    }, frequencyMsecs);
  }

  /**
   * Debugging function to help watch when something new takes focus.
   * It doesn't locate the root cause, but helps to point you in the right direction.
   * Debugging aid only - not meant for application code!
   */

  /* eslint-disable abinitio/check-listeners */
  static logGlobalFocusChanges_DEBUG() {
    document.addEventListener("focusout", e => {
      const ef = e;
      console.log(`focus out: `, ef.target); //`
    }, true);
    document.addEventListener("focusin", e => {
      const ef = e;
      console.log(`focus in: `, ef.target); //`
    }, true);
  }
}

//console.log(`calling FocusUtils.logGlobalFocusChanges_DEBUG`); //`
//FocusUtils.logGlobalFocusChanges_DEBUG()
_defineProperty(FocusUtils, "hasFocus", element => {
  return element.ownerDocument.activeElement === element;
});
/**
 * Utility class for implementing the "forward focus prop" pattern in react components. (you can google that)
 * For layered assemblies of components where the outer assembly needs to programatically
 * set focus on an input control, this allows for a clean separation between
 * the outer layers which need to set focus, and the inner layers which own the elements
 * that focus will be on.
 *
 * Caution - I've noticed situations where attempts to reuse a focusRef instance across multiple conditional
 * components has led to race condition bugs in focus behavior. -- jhoel
 * Recommendation: use one focusRef instance per component.
 * @author Jeff Hoel
 */
export let ForwardFocusRef = (_dec = action.bound, _class = (_ForwardFocusRef = class ForwardFocusRef {
  constructor(logDescription = "NoDescriptionGiven") {
    _initializerDefineProperty(this, "_current", _descriptor, this);
    _defineProperty(this, "haveRefProm", new DeferredValuePromise());
    // @ts-ignore: noUnusedLocals Used by commented out console.log lines.
    _defineProperty(this, "logDescription", "NoDescriptionGiven");
    makeObservable(this);
    this._current = null;
    //  console.log(`ForwardFocusRef constructor ${logDescription}`); //`
    this.logDescription = logDescription;
  }
  get current() {
    return this._current;
  }
  set current(current) {
    this._current = current;
  }
  /**
   * Bound method that can be passed to an input element.
   * eg
   * ref={this.props.forwardFocusRef.assignRef}
   */
  assignRef(r) {
    //console.log(`assignRef ${this.logDescription}`, r); //`
    this._current = r;
    if (r == null) this.haveRefProm = new DeferredValuePromise();else this.haveRefProm.resolveIt(r);
  }

  /**
   * Wait until a forwardFocus prop object has an element reference, and
   * once it does, give it focus.
   * To ensure this is not called repeatedly, a good place to invoke this
   * is from the componentDidMount() method of a react class.
   */
  async whenExistsTakeFocusOnce(disableBlurBeforeFocus = false) {
    // TODO - whenExistsTakeFocusOnce isn't really intended to be awaitable, so convert this to use an iife internally.
    //console.log(`ForwardFocusRef whenExistsTakeFocusOnce ${this.logDescription}`); //`
    let current = await this.haveRefProm.awaitValue();
    //console.log(`ForwardFocusRef setFocus now ${this.logDescription}`); //`
    current = NullUtils.assertDefined(current, this.logDescription);
    FocusUtils.setFocus(current, this.logDescription, disableBlurBeforeFocus);
  }

  /**
   * Sleep for zero time, then take focus.
   * This weird tactic allows setting focus on a component that hasnt been created
   * yet by a render method.
   * The sleep allows the render pass to occur so that the ref is set when it continues.
   */
  async sleepZeroThenFocus() {
    console.log(`ForwardFocusRef sleepZeroThenFocus ${this.logDescription}`); //`
    await PromiseUtils.sleepZero();

    //console.log(`ForwardFocusRef setFocus now ${this.logDescription}`); //`
    const current = NullUtils.assertIsDefined(this._current);
    FocusUtils.setFocus(current, this.logDescription);
  }

  /**
   * This strategy for setting focus on the DraftJs component works as of 2024.01.04.
   * The fact that sleep(1) makes a difference makes me suspicious about what other timing related calls are at play.
   */
  setFocusStrategy_debugJan4() {
    (async () => {
      // IIFE
      console.log(`setFocusStrategyJan4`); //`
      let current = await this.haveRefProm.awaitValue();
      await PromiseUtils.sleep(1);
      current.focus();
    })();
  }
  blur() {
    //console.log(`ForwardFocusRef blur ${this.logDescription}`); //`
    const current = NullUtils.assertIsDefined(this._current);
    current.blur();
  }
  blurIf() {
    //console.log(`ForwardFocusRef blurIf ${this.logDescription}`); //`
    if (this._current != null) this.blur();
  }
}, _defineProperty(_ForwardFocusRef, "acceptInputRef", forwardFocusRef => {
  return ref => {
    if (forwardFocusRef === undefined) return;
    if (ref === null) {
      forwardFocusRef.assignRef(null);
    } else {
      forwardFocusRef.assignRef(ref.inputElement);
    }
  };
}), _ForwardFocusRef), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_current", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "assignRef", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "assignRef"), _class.prototype), _class);
//# sourceMappingURL=FocusUtils.js.map
